/**
 * Goes through a columnOrder object and returns a new object without duplicate fields
 * @param {Object} columnOrder An object that contains the priority of the columns. Ex:
 * {
 *   3: 'title',
 *   7: 'description',
 * }
 * @returns a new columnOrder object without duplicate fields
 */
export const createUniqueColumnSortOrderObject = (columnOrder) => {
  const uniqueColumnOrderObject = {}
  const seen = {}

  Object.keys(columnOrder).forEach((key) => {
    const fieldName = columnOrder[key]
    if (!seen[fieldName]) {
      seen[fieldName] = true
      uniqueColumnOrderObject[key] = fieldName
    }
  })
  return uniqueColumnOrderObject
}

/**
 * Given a sorting object, return all the columns with the overrides of the sorting object
 * accounted for
 * @param {Object} columnOrder An object that contains the priority of the columns. Ex:
 * {
 *   3: 'title',
 *   7: 'description',
 * }
 * @param {Array} allColumns The array of all columns and their data unsorted
 * @returns A new Column array that is sorted based on the passed in Column Order object
 */
export const createPrioritySortedColumns = (columnOrder, allColumns) => {
  const allColumnsCopy = [...allColumns]
  const uniqueColumnOrder = createUniqueColumnSortOrderObject(columnOrder)
  let orderedFieldNames = Object.keys(columnOrder).map(
    (columnPriorityIndex) => columnOrder[columnPriorityIndex],
  )
  orderedFieldNames = [...new Set(orderedFieldNames)]
  const unsortedColumns = allColumnsCopy.filter((col) => !orderedFieldNames.includes(col.field))
  const totalColumnCount = allColumns.length

  const sortedColumns = []
  let index = 0

  while (index <= totalColumnCount) {
    const sortOrderPreference = uniqueColumnOrder[index]
    if (sortOrderPreference) {
      // there is a specified preference for the current index
      const desiredColumnIndex = allColumnsCopy.findIndex(
        (col) => col.field === sortOrderPreference,
      )
      if (desiredColumnIndex >= 0) {
        sortedColumns.push(allColumnsCopy[desiredColumnIndex])
        allColumnsCopy.splice(desiredColumnIndex, 1)
      } else if (unsortedColumns.length > 0) {
        // column not found, grab the next unsorted column
        const nextColumn = unsortedColumns.splice(0, 1)
        sortedColumns.push(nextColumn[0])
      }
    } else if (unsortedColumns.length > 0) {
      // there is no specified preference, grab the next unsorted column
      const nextColumn = unsortedColumns.splice(0, 1)
      sortedColumns.push(nextColumn[0])
    }

    index += 1
  }
  return sortedColumns
}

/**
 * Given a Column Sort Order object, intelligently returns a new sort order object that
 * contains the new preference, and intelligently shifts the other columns to accommodate
 * the new column
 * @param {*} prevColumnOrder The original Column Sort Order object
 * @param {*} newColumnOrder The new Column Preference as passed by MUIX
 * @returns A new Column Sort Order object
 */
export const createNewColumnOrder = (prevColumnOrder, newColumnOrder) => {
  const retVal = {}
  const desiredIndex = newColumnOrder.targetIndex
  const previousIndex = newColumnOrder.oldIndex

  let index = 0
  let forwardIndex = 0

  while (index < Object.keys(prevColumnOrder).length) {
    if (index === desiredIndex) {
      retVal[index] = newColumnOrder?.column?.field
    } else if (index === previousIndex) {
      if (desiredIndex > previousIndex) {
        forwardIndex += 1
      }
      retVal[index] = prevColumnOrder[forwardIndex]
      forwardIndex += 1
    } else {
      if ((index === previousIndex + 1) && desiredIndex < previousIndex) {
        forwardIndex += 1
      }
      retVal[index] = prevColumnOrder[forwardIndex]
      forwardIndex += 1
    }
    index += 1
  }
  // handle a case where a user is using an older url with fewer
  // columns, and moves one of the newer ones
  if (previousIndex >= index) {
    retVal[index] = prevColumnOrder[forwardIndex]
  }
  return retVal
}
